"use client"

/**
 * FadeIn
 *
 * A reusable React component for animating its children with a fade-in and upward motion when they enter the viewport.
 *
 * - Uses GSAP for animation.
 * - Supports custom delay, element type, and shared intersection observer for scroll-based triggers.
 * - Integrates with Lenis smooth scroll and a shared in-view observer for performance.
 *
 * Props:
 * - `element`: The HTML element or React component to render as the container (default: 'div').
 * - `children`: Content to animate in.
 * - `delay`: Optional delay (in seconds) before the animation starts.
 * - `sharedInViewId`: Optional observer ID for shared intersection observer (default: 'fadeIn25').
 * - `className`: Optional additional class names for the container.
 * - All other props are forwarded to the container element.
 *
 * Usage:
 * ```tsx
 * <FadeIn element="section" delay={0.2}>
 *   <h2>Animated Heading</h2>
 * </FadeIn>
 * ```
 *
 * Accessibility:
 * - Does not add extra DOM wrappers.
 * - Animation is visual only; ensure content is accessible without animation.
 *
 * See also:
 * - useSharedInView (for intersection observer logic)
 * - ScrollProvider (for Lenis integration)
 * - https://greensock.com/gsap/
 */

import type { ComponentPropsWithRef, ElementType, ReactNode } from "react"
import { useEffect, useRef, useState } from "react"
import cn from "classnames"
import { gsap } from "gsap"

import { useScroll } from "~/components/foundations/ScrollProvider"
import { type ObserverId, useSharedInView } from "~/hooks/useSharedInView"
import { EASE_OUT } from "~/styles/exports"

import s from "./FadeIn.module.scss"

type Props<TElement extends ElementType = "div"> = {
  element?: TElement
  children: ReactNode
  delay?: number
  sharedInViewId?: ObserverId
  className?: string
} & ComponentPropsWithRef<TElement>

export const FadeIn = <TElement extends ElementType = "div">({
  element = "div",
  children,
  delay,
  sharedInViewId = "fadeIn25",
  className,
}: Props<TElement>) => {
  const Container = element

  const { lenis } = useScroll()
  const [inViewId, setInViewId] = useState<ObserverId>("default")
  const contentRef = useRef<HTMLElement>(null)

  const { ref: sentinelRef, inView } = useSharedInView<HTMLDivElement>(
    inViewId,
    {
      once: true,
    },
  )

  useEffect(() => {
    if (!lenis) return

    const handleScroll = () => {
      setInViewId(sharedInViewId)
      lenis.off("scroll", handleScroll)
    }
    lenis.on("scroll", handleScroll)

    return () => {
      lenis.off("scroll", handleScroll)
    }
  }, [lenis, sharedInViewId])

  useEffect(() => {
    const element = contentRef.current
    if (!element) return

    if (inView) {
      const tween = gsap.to(element, {
        opacity: 1,
        y: 0,
        delay: delay || 0,
        duration: 1,
        ease: EASE_OUT,
      })

      return () => {
        tween.kill()
      }
    }
  }, [inView, contentRef, delay])

  return (
    <Container ref={contentRef} className={cn(s.fadeIn, className)}>
      {children}
      <span ref={sentinelRef} className={s.sentinel} aria-hidden />
    </Container>
  )
}
